import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastError, toastSuccess } from "../utils/toast";
import {
  CalendarDaysIcon,
  ArrowPathIcon,
  ArrowDownOnSquareIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import logoSrc from "../assets/logo/Logo-Planner-Turni.png";
import { registerMontserrat, FONT_MONTSERRAT } from "../utils/pdfFonts";

// Lazy loader per jsPDF/autotable, evita di caricare i moduli nel main bundle
let cachedJsPDF = null;
const loadJsPdf = async () => {
  if (cachedJsPDF) return cachedJsPDF;
  const [{ default: jsPDF }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  cachedJsPDF = jsPDF;
  return jsPDF;
};

const SLOT_LABELS = {
  infrasettimanale: "Infrasettimanale",
  fine_settimana: "Fine settimana",
};

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatLocalDate = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatWeekLabel = (date) =>
  date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const normalizeDateKey = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value).slice(0, 10);
  }
  return formatLocalDate(d);
};

const getWeeksInMonth = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const weeks = [];

  let cursor = startOfWeek(firstDay);
  while (cursor <= lastDay) {
    if (cursor.getMonth() === month) {
      weeks.push(new Date(cursor));
    }
    cursor.setDate(cursor.getDate() + 7);
  }
  return weeks;
};

const computeDefaultMonthDate = () => {
  const today = new Date();
  const defaultMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  if (today.getDate() > 10) {
    defaultMonth.setMonth(defaultMonth.getMonth() + 1);
  }
  defaultMonth.setHours(0, 0, 0, 0);
  return defaultMonth;
};

const buildAssignmentsMap = (assignmentsList = []) => {
  const map = {};
  assignmentsList.forEach((a) => {
    const key = `${normalizeDateKey(a.settimana)}-${a.slot}`;
    if (!map[key]) {
      map[key] = {
        settimana: normalizeDateKey(a.settimana),
        slot: a.slot,
        groups: [],
      };
    }
    if (a.group_id) {
      map[key].groups.push({
        group_id: a.group_id,
        group_nome: a.group_nome || null,
        generated: a.generated || false,
      });
    }
  });
  // assicura max 2 e nessun duplicato
  Object.keys(map).forEach((k) => {
    const unique = [];
    const seen = new Set();
    map[k].groups.forEach((g) => {
      const idStr = String(g.group_id);
      if (seen.has(idStr) || !g.group_id) return;
      seen.add(idStr);
      unique.push(g);
    });
    map[k].groups = unique.slice(0, 2);
  });
  return map;
};

const updateGroupArray = (
  current = [],
  index,
  groupId,
  groupNome,
  generated
) => {
  const arr = [...current];
  const idx = Number(index) || 0;
  // Normalizza lunghezza 2
  while (arr.length < 2) arr.push(null);
  if (groupId) {
    arr[idx] = {
      group_id: groupId,
      group_nome: groupNome || null,
      generated: generated || false,
    };
  } else {
    arr[idx] = null;
  }
  const cleaned = [];
  const seen = new Set();
  arr.forEach((g) => {
    if (!g || g.group_id == null) return;
    const idStr = String(g.group_id);
    if (seen.has(idStr)) return;
    seen.add(idStr);
    cleaned.push(g);
  });
  return cleaned.slice(0, 2);
};

const compareGroupArrays = (a = [], b = []) => {
  const norm = (list) =>
    list
      .filter((g) => g && g.group_id)
      .map((g) => String(g.group_id))
      .sort();
  const na = norm(a);
  const nb = norm(b);
  if (na.length !== nb.length) return false;
  for (let i = 0; i < na.length; i++) {
    if (na[i] !== nb[i]) return false;
  }
  return true;
};

const TurniPulizie = () => {
  const { user, activeCongregazione } = useAuth();
  const isAdmin =
    user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const [monthDate, setMonthDate] = useState(computeDefaultMonthDate);
  const [groups, setGroups] = useState([]);
  const [serverAssignments, setServerAssignments] = useState({});
  const [draftAssignments, setDraftAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canManage, setCanManage] = useState(true);
  const [permessiIds, setPermessiIds] = useState([]);
  const [volontari, setVolontari] = useState([]);
  const [savingPermessi, setSavingPermessi] = useState(false);
  const isServizioSpeciale = Boolean(activeCongregazione?.servizio_speciale);

  const monthKey = useMemo(
    () =>
      `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(
        2,
        "0"
      )}`,
    [monthDate]
  );

  const weeks = useMemo(() => getWeeksInMonth(monthDate), [monthDate]);

  const hasChanges = useMemo(() => {
    for (const week of weeks) {
      const weekKey = formatLocalDate(week);
      for (const slot of Object.keys(SLOT_LABELS)) {
        const key = `${weekKey}-${slot}`;
        const draftGroups = draftAssignments[key]?.groups || [];
        const savedGroups = serverAssignments[key]?.groups || [];
        if (!compareGroupArrays(draftGroups, savedGroups)) {
          return true;
        }
      }
    }
    return false;
  }, [weeks, draftAssignments, serverAssignments]);

  const fetchData = useCallback(async () => {
    if (isServizioSpeciale) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/turni-pulizie?mese=${monthKey}`);
      const groupsList = res.data?.groups || [];
      const assignmentsList = res.data?.assignments || [];
      const map = buildAssignmentsMap(assignmentsList);
      setServerAssignments(map);
      setDraftAssignments(map);
      setGroups(groupsList);
      setPermessiIds(
        Array.isArray(res.data?.permessi)
          ? res.data.permessi.map((v) => (v == null ? null : Number(v)))
          : []
      );
      setCanManage(res.data?.canManage !== false);
    } catch (error) {
      console.error("Errore caricamento turni pulizie:", error);
      if (error.response?.status === 403) {
        setCanManage(false);
        toastError("Non hai i permessi per gestire i turni pulizie.");
      } else {
        toastError(
          error.response?.data?.message ||
            "Impossibile caricare i turni pulizie"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [monthKey, isServizioSpeciale]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchVolontari = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get("/volontari", { params: { limit: 500 } });
      const list = res.data?.volontari || res.data?.items || res.data || [];
      setVolontari(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Errore caricamento volontari per permessi pulizie:", error);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchVolontari();
  }, [fetchVolontari]);

  const handleChangeMonth = (direction) => {
    setMonthDate((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + direction);
      return next;
    });
  };

  const updateDraft = useCallback(
    (settimana, slot, index, groupId, generated = false) => {
      const weekKey = normalizeDateKey(settimana);
      const selectedGroup =
        groupId && groups.find((g) => String(g.id) === String(groupId));
      const idx = Number(index) || 0;
      setDraftAssignments((prev) => ({
        ...prev,
        [`${weekKey}-${slot}`]: {
          settimana: weekKey,
          slot,
          groups: updateGroupArray(
            prev[`${weekKey}-${slot}`]?.groups ||
              serverAssignments[`${weekKey}-${slot}`]?.groups ||
              [],
            idx,
            groupId,
            selectedGroup?.nome || null,
            generated
          ),
        },
      }));
    },
    [groups, serverAssignments]
  );

  const handleReset = async (settimana, slot) => {
    const key = `${settimana}-${slot}`;
    const hasServerAssignment = Boolean(serverAssignments[key]);
    if (!hasServerAssignment && !draftAssignments[key]) {
      return;
    }
    setSaving(true);
    try {
      if (hasServerAssignment) {
        await api.delete(
          `/turni-pulizie/assign?settimana=${settimana}&slot=${slot}`
        );
      }
      setDraftAssignments((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (hasServerAssignment) {
        setServerAssignments((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
      toastSuccess(
        hasServerAssignment
          ? "Turno pulizie rimosso"
          : "Assegnazione annullata"
      );
    } catch (error) {
      console.error("Errore reset turno pulizie:", error);
      toastError(
        error.response?.data?.message ||
          "Errore nella rimozione del turno pulizie"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAutoCompile = async () => {
    setSaving(true);
    try {
      const seen = new Set();
      const preassignments = [];
      Object.values(draftAssignments || {}).forEach((a) => {
        if (!a || !Array.isArray(a.groups)) return;
        const weekKey = normalizeDateKey(a.settimana);
        (a.groups || []).forEach((g) => {
          if (!g?.group_id) return;
          const key = `${weekKey}-${a.slot}-${g.group_id}`;
          if (seen.has(key)) return;
          seen.add(key);
          preassignments.push({
            settimana: weekKey,
            slot: a.slot,
            group_id: g.group_id,
          });
        });
      });

      const res = await api.post("/turni-pulizie/genera", {
        mese: monthKey,
        preview: true,
        preassignments,
      });
      const assignmentsList = res.data?.assignments || [];
      const map = buildAssignmentsMap(assignmentsList);
      setDraftAssignments((prev) => ({ ...prev, ...map }));
      toastSuccess("Autocompilazione preparata (salva per confermare)");
    } catch (error) {
      console.error("Errore generazione turni pulizie:", error);
      toastError(
        error.response?.data?.message ||
          "Errore nella compilazione automatica"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const changes = [];
      weeks.forEach((week) => {
        const weekKey = formatLocalDate(week);
        Object.keys(SLOT_LABELS).forEach((slot) => {
          const key = `${weekKey}-${slot}`;
          const draft = draftAssignments[key];
          const saved = serverAssignments[key];
          const draftGroups = draft?.groups || [];
          const savedGroups = saved?.groups || [];
          if (!compareGroupArrays(draftGroups, savedGroups)) {
            if (draftGroups.length > 0) {
              const payloadGroups = draftGroups
                .filter((g) => g?.group_id)
                .map((g) => ({
                  id: g.group_id,
                  generated: Boolean(g.generated),
                }))
                .slice(0, 2);
              changes.push({
                action: "assign",
                settimana: weekKey,
                slot,
                groups: payloadGroups,
              });
            } else if (saved) {
              changes.push({
                action: "delete",
                settimana: weekKey,
                slot,
              });
            }
          }
        });
      });

      for (const change of changes) {
        if (change.action === "assign") {
          await api.post("/turni-pulizie/assign", {
            settimana: change.settimana,
            slot: change.slot,
            groups: change.groups,
          });
        } else {
          await api.delete(
            `/turni-pulizie/assign?settimana=${change.settimana}&slot=${change.slot}`
          );
        }
      }

      await fetchData();
      toastSuccess("Turni pulizie salvati");
    } catch (error) {
      console.error("Errore salvataggio turni pulizie:", error);
      toastError(
        error.response?.data?.message || "Errore durante il salvataggio"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSavePermessi = async () => {
    setSavingPermessi(true);
    try {
      const payload = permessiIds.filter((id) => id != null).slice(0, 2);
      await api.put("/turni-pulizie/permessi", {
        volontari: payload,
      });
      setPermessiIds(payload);
      toastSuccess("Referenti pulizie aggiornati");
    } catch (error) {
      console.error("Errore salvataggio permessi pulizie:", error);
      toastError(
        error.response?.data?.message ||
          "Errore nel salvataggio dei referenti"
      );
    } finally {
      setSavingPermessi(false);
    }
  };

  const volontariMap = useMemo(() => {
    const map = new Map();
    volontari.forEach((v) => {
      map.set(v.id, v);
    });
    return map;
  }, [volontari]);

  const referentiSelezionati = useMemo(
    () => permessiIds.map((id) => volontariMap.get(id)).filter(Boolean),
    [permessiIds, volontariMap]
  );

  const exportPdf = async () => {
    const JsPDF = await loadJsPdf();
    const doc = new JsPDF("portrait", "mm", "a4");
    registerMontserrat(doc);
    doc.setFont(FONT_MONTSERRAT, "normal");

    const headerColor = [51, 102, 204];
    const accentColor = [92, 121, 255];

    const drawHeader = () => {
      doc.setFillColor(...headerColor);
      doc.rect(0, 0, 210, 30, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont(FONT_MONTSERRAT, "bold");
      doc.setFontSize(16);
      doc.text(
        `Turni Pulizie - ${monthDate.toLocaleDateString("it-IT", {
          month: "long",
          year: "numeric",
        })}`,
        14,
        18
      );
      if (logoSrc) {
        // Lazy image load via fetch -> dataURL
      }
      doc.setTextColor(0, 0, 0);
    };

    const addLogo = async () => {
      try {
        const resp = await fetch(logoSrc);
        const blob = await resp.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        // Calcola proporzioni originali per evitare deformazioni
        const imgDims = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve({ w: img.width, h: img.height });
          img.onerror = reject;
          img.src = dataUrl;
        });

        const maxSize = 18; // lato massimo consentito (mm) dentro la sfera
        const ratio = Math.min(
          maxSize / imgDims.w,
          maxSize / imgDims.h
        );
        const renderW = imgDims.w * ratio;
        const renderH = imgDims.h * ratio;
        const centerX = 185;
        const centerY = 14;

        // Sfera bianca di sfondo per dare contrasto al logo
        doc.setFillColor(255, 255, 255);
        doc.circle(centerX, centerY, 11, "F");
        doc.addImage(
          dataUrl,
          "PNG",
          centerX - renderW / 2,
          centerY - renderH / 2,
          renderW,
          renderH
        );
      } catch (_err) {
        // If logo fails, continue without blocking export
      }
    };

    const renderBody = () => {
      let y = 42;
      doc.setFont(FONT_MONTSERRAT, "normal");
      doc.setFontSize(12);
      weeks.forEach((week, idx) => {
        const settimanaKey = `${formatLocalDate(week)}`;
        if (y > 260) {
          doc.addPage();
          drawHeader();
          y = 42;
        }
        doc.setTextColor(...accentColor);
        doc.text(`Settimana del ${formatWeekLabel(week)}`, 14, y);
        y += 6;
        doc.setTextColor(40, 40, 40);
        Object.entries(SLOT_LABELS).forEach(([slotKey, label]) => {
          const row = draftAssignments[`${settimanaKey}-${slotKey}`];
          const groupLabel =
            row?.groups?.length > 0
              ? row.groups
                  .map((g) => g?.group_nome)
                  .filter(Boolean)
                  .join(", ")
              : "—";
          doc.text(`${label}: ${groupLabel}`, 20, y);
          y += 6;
        });
        if (idx < weeks.length - 1) {
          doc.setDrawColor(230, 230, 230);
          doc.line(14, y + 1, 196, y + 1);
        }
        y += 6;
      });
    };

    const build = async () => {
      drawHeader();
      await addLogo();
      renderBody();
      doc.save(`turni-pulizie-${monthKey}.pdf`);
    };

    build();
  };

  if (!isAdmin) {
    if (!canManage) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">
              Non hai i permessi per accedere ai turni pulizie.
            </p>
          </div>
        </div>
      );
    }
  }

  if (isServizioSpeciale) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            I turni pulizie non sono disponibili per le congregazioni speciali.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {saving ? (
        <div className="flex items-center gap-2 bg-primary-50 border border-primary-200 text-primary-700 px-3 py-2 rounded-md shadow-sm">
          <span className="inline-flex h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <span>Operazione in corso... Attendere qualche secondo.</span>
        </div>
      ) : null}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Turni Pulizie</h1>
          <p className="text-gray-600 mt-1">
            Organizza i turni di pulizia settimanali per i gruppi di servizio.
          </p>
          <div className="accent-line mt-3" />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleChangeMonth(-1)}
            className="p-2 border rounded-full hover:bg-gray-100"
            aria-label="Mese precedente"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-50 border text-gray-700">
            <CalendarDaysIcon className="h-5 w-5 text-primary-600" />
            <span className="font-medium capitalize">
              {monthDate.toLocaleDateString("it-IT", {
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>
          <button
            onClick={() => handleChangeMonth(1)}
            className="p-2 border rounded-full hover:bg-gray-100"
            aria-label="Mese successivo"
          >
            <ArrowRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleAutoCompile}
          disabled={saving || loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Elaborazione...
            </span>
          ) : (
            <>
              <ArrowPathIcon className="h-5 w-5" />
              Compila automaticamente
            </>
          )}
        </button>
        <button
          onClick={async () => {
            if (
              window.confirm(
                "Vuoi cancellare tutti i turni di pulizia del mese corrente?"
              )
            ) {
              setSaving(true);
              try {
                await api.delete(`/turni-pulizie/mese?mese=${monthKey}`);
                setDraftAssignments({});
                setServerAssignments({});
                toastSuccess("Turni di pulizia del mese resettati");
              } catch (error) {
                console.error("Errore reset mese turni pulizie:", error);
                toastError(
                  error.response?.data?.message ||
                    "Errore durante il reset del mese"
                );
              } finally {
                setSaving(false);
              }
            }
          }}
          disabled={saving || loading}
          className="btn-danger flex items-center gap-2 px-4 py-2 w-full sm:w-auto whitespace-nowrap"
        >
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Reset in corso...
            </span>
          ) : (
            <>
              <TrashIcon className="h-4 w-4" />
              Reset mese
            </>
          )}
        </button>
        <button
          onClick={exportPdf}
          className="inline-flex items-center gap-2 px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
        >
          <ArrowDownOnSquareIcon className="h-5 w-5" />
          Esporta PDF mensile
        </button>
      </div>

      {isAdmin ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-3 relative overflow-hidden">
          <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Referenti Pulizie
              </p>
              <p className="text-xs text-gray-500">
                Seleziona fino a 2 proclamatori che possono gestire i turni e scaricare il PDF.
              </p>
            </div>
            <button
              onClick={handleSavePermessi}
              disabled={savingPermessi}
              className="px-3 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              Salva referenti
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {[0, 1].map((idx) => {
              const selectedId = permessiIds[idx] || "";
              return (
                <select
                  key={idx}
                  value={selectedId}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : null;
                    setPermessiIds((prev) => {
                      const next = [...prev];
                      next[idx] = val;
                      while (next.length < 2) next.push(null);
                      return next.slice(0, 2);
                    });
                  }}
                  className="border rounded-md px-3 py-2 text-sm min-w-[220px]"
                  disabled={savingPermessi}
                >
                  <option value="">Seleziona referente</option>
                  {volontari.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nome} {v.cognome}
                    </option>
                  ))}
                </select>
              );
            })}
          </div>
          {referentiSelezionati.length > 0 ? (
            <div className="flex flex-wrap gap-2 text-sm text-gray-700">
              {referentiSelezionati.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-3 py-1 rounded-full border border-primary-100"
                >
                  {r.nome} {r.cognome}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="text-gray-600">Caricamento turni pulizie...</div>
      ) : (
        <div className="space-y-4">
          {hasChanges ? (
            <div className="flex flex-wrap gap-3 items-center">
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Salvataggio...
                  </span>
                ) : (
                  "Salva modifiche"
                )}
              </button>
              <button
                onClick={() => setDraftAssignments(serverAssignments)}
                disabled={saving || loading}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annulla modifiche
              </button>
            </div>
          ) : null}
          {weeks.map((week) => {
            const settimana = formatLocalDate(week);
            return (
              <div
                key={settimana}
                className="border rounded-lg bg-white shadow-sm relative overflow-hidden"
              >
                <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
                <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      Settimana del {formatWeekLabel(week)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Seleziona il gruppo per ogni turno di pulizia.
                    </p>
                  </div>
                </div>
                <div className="divide-y">
                  {Object.entries(SLOT_LABELS).map(([slotKey, label]) => {
                    const assignment =
                      draftAssignments[`${settimana}-${slotKey}`];
                    const groupsForSlot = assignment?.groups || [];
                    const statusLabel =
                      groupsForSlot.length === 0
                        ? "Non assegnato"
                        : groupsForSlot.some((g) => g?.generated)
                        ? "Assegnato automaticamente"
                        : "Assegnato manualmente";
                    return (
                      <div
                        key={slotKey}
                        className="px-4 py-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {label}
                          </p>
                          <p className="text-xs text-gray-500">
                            {statusLabel}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {[0, 1].map((idx) => {
                            const selectedGroup = groupsForSlot[idx];
                            const selectedValue =
                              selectedGroup?.group_id != null
                                ? String(selectedGroup.group_id)
                                : "";
                            return (
                              <select
                                key={idx}
                                value={selectedValue}
                                onChange={(e) =>
                                  updateDraft(
                                    settimana,
                                    slotKey,
                                    idx,
                                    e.target.value
                                      ? Number(e.target.value)
                                      : null,
                                    false
                                  )
                                }
                                className="border rounded-md px-3 py-2 text-sm min-w-[220px]"
                                disabled={saving}
                              >
                                <option value="">
                                  {idx === 0
                                    ? "Seleziona gruppo 1"
                                    : "Seleziona gruppo 2 (opzionale)"}
                                </option>
                                {groups.map((group) => (
                                  <option
                                    key={group.id}
                                    value={String(group.id)}
                                  >
                                    {group.nome}
                                  </option>
                                ))}
                              </select>
                            );
                          })}
                          {groupsForSlot.length > 0 ? (
                            <button
                              onClick={() =>
                                handleReset(settimana, slotKey)
                              }
                              className="text-sm text-red-600 px-3 py-2 border rounded-md hover:bg-red-50"
                              disabled={saving}
                            >
                              Reset
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TurniPulizie;
